import { AlertTriangle, ShieldAlert } from "lucide-react";

import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDateTime } from "@/lib/date";

import { useKbOverview, type FacetRow, type SyncStatus } from "../api";

/** The sync runs nightly Mon–Sat (04:00 MT). The README's own rule: a status
 *  older than 36 hours means the cron container is down. Sunday is skipped on
 *  purpose, so Monday morning can legitimately show ~28h; 36 clears that. */
const SYNC_STALE_HOURS = 36;

/** Crawl dates arrive as full ISO timestamps from the pipeline, not as dates —
 *  rendering one raw puts `2026-09-18T17:57:15+00:00` in a tile built for ten
 *  characters. The time of day is never the point here; the day is. */
function dayOnly(value: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

function FacetList({
  title,
  description,
  rows,
  renderLabel,
  onPick,
}: {
  title: string;
  description?: string;
  rows: FacetRow[];
  renderLabel?: (value: string) => React.ReactNode;
  onPick?: (value: string) => void;
}) {
  const total = rows.reduce((sum, r) => sum + r.count, 0) || 1;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing indexed yet.</p>
        ) : null}
        {rows.slice(0, 12).map((r) => (
          <div key={r.value} className="flex items-center gap-3 text-sm">
            <span className="w-44 shrink-0 truncate">
              {onPick ? (
                <Button
                  variant="link"
                  className="h-auto p-0 text-sm font-normal"
                  onClick={() => onPick(r.value)}
                  title={r.value}
                >
                  {renderLabel ? renderLabel(r.value) : r.value}
                </Button>
              ) : renderLabel ? (
                renderLabel(r.value)
              ) : (
                r.value
              )}
            </span>
            {/* Proportion bar — width is data-driven, so it stays inline. */}
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <span
                className="block h-full rounded-full bg-brand"
                style={{ width: `${Math.max(2, (r.count / total) * 100)}%` }}
              />
            </span>
            <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
              {r.count.toLocaleString()}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function authError(sync: SyncStatus): string | null {
  const err = sync.errors.find((e) => e.stage === "auth");
  return err && typeof err.error === "string" ? err.error : null;
}

/** The one banner the page shows, picked by severity. Returns null when the
 *  last run is a good one and recent. */
function SyncBanner({ sync }: { sync: SyncStatus }) {
  if (!sync.available) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Pipeline status unavailable</AlertTitle>
        <AlertDescription>
          The index below is live, but the sync job&rsquo;s status could not be
          read
          {sync.reason ? ` (${sync.reason})` : ""}. Freshness, run history and
          the review desk are unknown until it is back.
        </AlertDescription>
      </Alert>
    );
  }
  if (sync.result === "auth_failed") {
    const detail = authError(sync);
    return (
      <Alert variant="danger">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>The nightly crawl could not sign in</AlertTitle>
        <AlertDescription>
          The service account was refused at support.cinch.io on{" "}
          {fmtDateTime(sync.finished_at)}. The run stopped, changed nothing and
          deleted nothing, so the agent is answering from the previous crawl.
          Fix the password (or MFA) on the service account, clear the browser
          profile, and re-run.
          {detail ? (
            <div className="mt-2 rounded bg-danger/10 px-2 py-1 font-mono text-xs">
              {detail}
            </div>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  }
  if (sync.result === "failed") {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>The last run failed</AlertTitle>
        <AlertDescription>
          {sync.error_count} error{sync.error_count === 1 ? "" : "s"} recorded
          at {fmtDateTime(sync.finished_at)}. See the Runs tab and the last-run
          card below.
        </AlertDescription>
      </Alert>
    );
  }
  if (sync.age_hours === null || sync.age_hours > SYNC_STALE_HOURS) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>The nightly sync is not running</AlertTitle>
        <AlertDescription>
          Last completed run was {fmtDateTime(sync.finished_at)}
          {sync.age_hours !== null
            ? ` — ${Math.round(sync.age_hours / 24)} days ago`
            : ""}
          . Search still works, but it is answering from that crawl: everything
          edited in HubSpot since is invisible to the agent.
        </AlertDescription>
      </Alert>
    );
  }
  if (sync.result === "incomplete_crawl") {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Last crawl was incomplete</AlertTitle>
        <AlertDescription>
          Too many pages failed to fetch, so no deletions were applied (a flaky
          night must never read as a mass deletion). Changed articles still
          indexed; the next run will fill in the rest.
        </AlertDescription>
      </Alert>
    );
  }
  if (sync.result === "partial") {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Some articles failed to index</AlertTitle>
        <AlertDescription>
          {sync.error_count} error{sync.error_count === 1 ? "" : "s"} during
          indexing. Unchanged articles are unaffected; the failed ones retry on
          the next run.
        </AlertDescription>
      </Alert>
    );
  }
  // A run can report `ok` having never signed in — an index-only or reconcile
  // pass does exactly that, and so does a crawl that quietly found nothing.
  // Either way the articles are whatever the LAST successful crawl left, which
  // is the one thing this page exists to make visible, so it gets said out loud
  // rather than left to whoever reads the detail table at the bottom.
  if (sync.auth_ok === false) {
    return (
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>The last run never signed in</AlertTitle>
        <AlertDescription>
          It finished without errors, but it did not authenticate against
          support.cinch.io and discovered no articles, so nothing was re-read.
          What is indexed below is whatever the last successful crawl left
          behind. Harmless for an index-only or reconcile pass; a problem if a
          full crawl was expected.
        </AlertDescription>
      </Alert>
    );
  }
  return null;
}

function runTone(
  sync: SyncStatus,
): "positive" | "warning" | "danger" | "neutral" {
  if (!sync.available) return "neutral";
  if (sync.result === "auth_failed" || sync.result === "failed")
    return "danger";
  if (sync.age_hours !== null && sync.age_hours > SYNC_STALE_HOURS)
    return "danger";
  if (sync.result === "partial" || sync.result === "incomplete_crawl")
    return "warning";
  // Never green on a run that didn't sign in, whatever it called itself.
  if (sync.auth_ok === false) return "warning";
  return "positive";
}

export function OverviewTab({
  onPickCategory,
  onPickStatus,
}: {
  onPickCategory: (category: string) => void;
  onPickStatus: (status: string) => void;
}) {
  const { data, isPending, error } = useKbOverview();

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Index unreachable</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
          <div className="mt-1 text-xs">
            The Qdrant collection lives on the Spark host — this usually means
            the VPN is down, the container is stopped, or the collection has not
            been created by a first ingest yet.
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  const { health, sync, categories, article_statuses: statuses } = data;
  const tone = runTone(sync);

  return (
    <div className="flex flex-col gap-6">
      <SyncBanner sync={sync} />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Articles indexed"
          value={health.articles.toLocaleString()}
          sub={`${health.chunks.toLocaleString()} sections · ${health.internal_articles} internal`}
          info={
            <>
              One search entry per article section (heading-aware chunks of up
              to 500 tokens), plus one full-article record per article.
              &ldquo;Internal&rdquo; counts articles the index still holds with
              the internal flag — the pipeline drops these before indexing, so
              this should read 0.
            </>
          }
          infoTitle="What is counted"
        />
        <KpiTile
          label="Last crawl"
          value={dayOnly(data.newest_crawl)}
          tone={tone}
          sub={
            data.oldest_crawl &&
            dayOnly(data.oldest_crawl) !== dayOnly(data.newest_crawl)
              ? `oldest article crawled ${dayOnly(data.oldest_crawl)}`
              : sync.available && sync.finished_at
                ? `run finished ${fmtDateTime(sync.finished_at)}`
                : "run time unknown"
          }
        />
        <KpiTile
          label="Last run"
          value={
            <StatusBadge value={sync.available ? sync.result : "unknown"} />
          }
          tone={tone}
          sub={
            sync.available
              ? `${sync.articles_changed ?? 0} changed · ${sync.articles_deleted ?? 0} deleted · ${
                  sync.duration_s !== null
                    ? `${Math.round(sync.duration_s / 60)} min`
                    : "—"
                }`
              : "status unavailable"
          }
        />
        <KpiTile
          label="Embedding model"
          value={
            <span className="font-mono text-lg">{health.embed_model}</span>
          }
          sub={`${health.vector_size ?? "?"}d ${health.distance ?? ""}${
            health.sparse ? " + BM25" : ""
          } · from ${health.embed_model_source}`}
          info={
            <>
              Search queries are embedded with this model, which must be the one
              the pipeline built the index with — a query in the wrong vector
              space returns confident nonsense. &ldquo;From pipeline&rdquo;
              means it follows what the last run reported; &ldquo;from
              env&rdquo; is an operator override; &ldquo;from default&rdquo;
              means the pipeline status is unavailable and the built-in default
              is in use.
            </>
          }
          infoTitle="Why this matters"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <FacetList
          title="Articles by category"
          description="Top-level HubSpot category. Click one to browse it."
          rows={categories}
          onPick={onPickCategory}
        />
        <FacetList
          title="Sync status by article"
          description={
            data.articles_source === "pipeline"
              ? "From the pipeline's article table. Anything but Indexed clears on the next run."
              : "Needs the pipeline status API — unavailable right now."
          }
          rows={statuses}
          renderLabel={(v) => <StatusBadge value={v} />}
          onPick={onPickStatus}
        />
      </div>

      {sync.available ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Last run in detail</CardTitle>
            <p className="text-sm text-muted-foreground">
              {sync.run_id ?? "run"} · started {fmtDateTime(sync.started_at)} ·
              finished {fmtDateTime(sync.finished_at)}
              {sync.corpus_commit ? ` · corpus ${sync.corpus_commit}` : ""}
            </p>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Signed in"
                value={
                  sync.auth_ok === null ? "—" : sync.auth_ok ? "yes" : "NO"
                }
              />
              <Stat
                label="Crawl complete"
                value={
                  sync.crawl_complete === null
                    ? "—"
                    : sync.crawl_complete
                      ? "yes"
                      : "no"
                }
              />
              <Stat
                label="Articles discovered"
                value={sync.articles_discovered}
              />
              <Stat label="Articles changed" value={sync.articles_changed} />
              <Stat
                label="Articles unchanged"
                value={sync.articles_unchanged}
              />
              <Stat label="Articles deleted" value={sync.articles_deleted} />
              <Stat label="Documents indexed" value={sync.docs_indexed} />
              <Stat label="Documents skipped" value={sync.docs_skipped} />
              <Stat label="Sections upserted" value={sync.chunks_upserted} />
              <Stat label="Points in collection" value={sync.points_total} />
              <Stat label="Embedding dim" value={sync.embed_dim} />
              <Stat label="Errors" value={sync.error_count} />
            </dl>
            {sync.errors.length > 0 ? (
              <div className="mt-4">
                <p className="mb-1 text-xs font-medium text-muted-foreground">
                  First {Math.min(sync.errors.length, 8)} of{" "}
                  {sync.errors.length} errors
                </p>
                <ul className="flex flex-col gap-1">
                  {sync.errors.slice(0, 8).map((e, i) => (
                    <li
                      key={i}
                      className="truncate font-mono text-xs"
                      title={JSON.stringify(e)}
                    >
                      <span className="text-muted-foreground">
                        [{String(e.stage ?? "?")}]
                      </span>{" "}
                      {String(e.url ?? e.document_id ?? "")}{" "}
                      {String(e.error ?? "")}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: number | string | null | undefined;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b py-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">
        {value === null || value === undefined
          ? "—"
          : typeof value === "number"
            ? value.toLocaleString()
            : value}
      </dd>
    </div>
  );
}
